import type { BlockCardAttributes, BlockCardAttributesSelf, BlockCardItemAttributeSpecSelf, BlockCardOptions } from "./types";
import { isNodeSelection, mergeAttributes, Node, VueNodeViewRenderer } from "@tiptap/vue-3";
import { setHttpsProtocolIfNeeded } from "@/common/lib/string";
import { enterKeyHandler } from "../enterKeyHandler";
import BlockCard from "./BlockCard.vue";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    blockCard: {
      /**
       * Set cursor after
       */
      setCursorAfter: () => ReturnType;

      /**
       * Set block card
       */
      setBlockCard: (attributes: BlockCardAttributesSelf) => ReturnType;

      /**
       * Unlinks inline card
       */
      unlinkBlockCard: (text?: string) => ReturnType;
    };
  }
}

export default Node.create<BlockCardOptions>({
  name: "blockCard",
  group: "block",
  inline: false,
  draggable: true,

  addAttributes() {
    return {
      url: {
        default: null,
      },
      text: {
        default: null,
        rendered: false,
      },
    } satisfies BlockCardItemAttributeSpecSelf;
  },

  parseHTML() {
    return [{
      tag: `div[data-type="${this.name}"]`,
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as BlockCardAttributes;
    return [
      "div",
      mergeAttributes({
        "data-type": this.name,
        "data-url": attrs.url,
      }, HTMLAttributes),
      attrs.text || attrs.url,
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(BlockCard);
  },

  renderText({ node }) {
    const attrs = node.attrs as BlockCardAttributes;
    return attrs.text || attrs.url;
  },

  addProseMirrorPlugins() {
    return [
      enterKeyHandler({
        type: this.type,
        getUrl(attributes) {
          const attrs = attributes as unknown as BlockCardAttributes;
          return attrs.url;
        },
      }),
    ];
  },

  addCommands() {
    return {
      setCursorAfter: () => ({ commands, chain, state }) => {
        const { selection } = state;
        const { $to } = selection;
        if ($to.nodeAfter) {
          if ($to.nodeAfter.isTextblock) {
            commands.setTextSelection($to.pos + 1);
          } else if ($to.nodeAfter.isBlock) {
            commands.setNodeSelection($to.pos);
          } else {
            commands.setTextSelection($to.pos);
          }
        } else {
          // add trailing node if it’s the end of the document
          commands.insertTrailingParagraphIfNeeded();
        }

        return chain()
          .scrollIntoView()
          .run();
      },

      setBlockCard: attrs => ({ commands, chain, state }) => {
        // normalize url to include protocol if needed
        attrs.url = setHttpsProtocolIfNeeded(attrs.url);

        const node = { type: this.name, attrs };
        const { selection } = state;

        // fully replace if it's a single node within parent
        const parent = selection.$from.parent;
        if (parent.childCount === 1) {
          return chain()
            .selectParentNode()
            .command(({ tr }) => {
              tr.replaceSelectionWith(this.type.create(attrs));
              return true;
            })
            .run();
        }

        return commands.insertContent(node);
      },

      unlinkBlockCard: text => ({ chain, state }) => {
        const { selection } = state;

        if (!isNodeSelection(selection) || selection.node.type !== this.type) {
          return false;
        }

        const node = selection.node;
        const attrs = node.attrs as BlockCardAttributes | undefined;
        if (!attrs) return false;

        const textToSet = text || attrs.url;
        const textNode = state.schema.text(textToSet);

        return chain()
          .command(({ tr }) => {
            tr.replaceSelectionWith(textNode);
            return true;
          })
          .setMeta("preventAutolink", true)
          .run();
      },
    };
  },
});
